import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import {
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ClipboardList,
  ChevronRight,
  Search,
  Ticket,
  UserPlus,
  Waves,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { errorMessage } from '@/api/errors'
import {
  useCurrentPermissions,
  useGymMemberServiceCredits,
  useGymMembers,
  useServiceAvailability,
  useServiceBookingMutations,
  useServices,
} from '@/api/queries'
import type {
  Service,
  ServiceAccessMode,
  ServiceKind,
  ServiceResourceAvailability,
  ServiceSlot,
  Uuid,
} from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { ServiceBookingsPanel } from '@/features/services/bookings-panel'
import { ServiceCatalogPanel } from '@/features/services/catalog-panel'
import { plural } from '@/lib/format'

/**
 * I servizi con accesso dedicato — SRV-01…04.
 *
 * ## La domanda è «questo lettino è libero alle 18?»
 *
 * Non «quali prenotazioni ci sono». La griglia la compone il server
 * (`/availability`) e questa pagina la mostra: fuori dalle fasce del servizio gli
 * slot **non compaiono**, perché «chiuso» e «pieno» sono due risposte diverse e
 * la seconda suggerisce di riprovare fra un'ora.
 *
 * ## Un giorno per volta, una risorsa per riga
 *
 * La sauna ha tre cabine e il solarium cinque lettini: la riga è la risorsa,
 * perché è su quella che il posto si occupa. «La sauna alle 18» non è occupabile
 * una volta sola, «la cabina 2 alle 18» sì.
 *
 * ## Chi legge e chi scrive
 *
 * `service:read` mostra la griglia, `service:write` la rende cliccabile. Il
 * trainer ha solo la prima, e nascondere il gesto è meglio che offrirlo e
 * incassare un `403`.
 *
 * ## Ciò che questa pagina **non** fa, dichiarato invece che lasciato cercare
 *
 * Non crea servizi né risorse: quelle rotte esistono (SRV-01) e la loro
 * schermata no. Qui si prenota e si disdice, che è il gesto quotidiano; il
 * catalogo si scrive una volta e per ora si scrive dall'API.
 */
export const Route = createFileRoute('/_app/servizi/')({
  component: ServicesPage,
})

const NO_RESOURCES: ServiceResourceAvailability[] = []

const KIND_LABELS: Record<ServiceKind, string> = {
  gym_floor: 'Sala pesi',
  pool: 'Piscina',
  sauna: 'Sauna',
  solarium: 'Solarium',
  court: 'Campo',
  functional_area: 'Area funzionale',
  personal_training: 'Personal training',
  other: 'Altro',
}

const ACCESS_LABELS: Record<ServiceAccessMode, string> = {
  included: 'Incluso',
  consumption: 'A consumo',
  booking: 'Su prenotazione',
  booking_and_consumption: 'Prenotazione + carnet',
}

/** Solo questi due si prenotano: sugli altri la griglia non ha senso. */
function isBookable(service: Service): boolean {
  return service.accessMode === 'booking' || service.accessMode === 'booking_and_consumption'
}

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function ServicesPage() {
  const permissions = useCurrentPermissions()
  const canWrite = permissions.has('service:write')

  const services = useServices()
  const rows = services.data?.data ?? []
  const bookable = rows.filter(isBookable)

  const [selectedId, setSelectedId] = useState<Uuid | null>(null)
  const [duration, setDuration] = useState<number | undefined>(undefined)
  const [day, setDay] = useState(() => new Date())
  const [slot, setSlot] = useState<{
    resourceId: Uuid
    resourceName: string
    startsAt: string
    durationMinutes: number
    timezone?: string
  } | null>(null)
  const [cancelling, setCancelling] = useState<{ bookingId: Uuid; label: string } | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  // La prima volta si sceglie da soli il servizio: con uno solo prenotabile,
  // chiedere un clic per arrivare all'unica griglia possibile è un clic in più.
  const selected = selectedId ?? bookable[0]?.id ?? null
  const service = rows.find((row) => row.id === selected) ?? null

  const date = toIsoDate(day)
  const availability = useServiceAvailability(selected, date, duration)
  const mutations = useServiceBookingMutations()

  // `?? []` crea un array nuovo a ogni render, e un `useMemo` che lo osserva non
  // memorizza niente: la lista vuota è quindi una costante di modulo.
  const resources = availability.data?.data ?? NO_RESOURCES
  const freeCount = useMemo(
    () => resources.reduce((sum, row) => sum + row.slots.filter((s) => s.available).length, 0),
    [resources],
  )

  const label = day.toLocaleDateString('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  async function book(gymMemberId: Uuid) {
    if (slot === null || service === null) return
    setFailure(null)
    try {
      await mutations.book.mutateAsync({
        serviceId: service.id,
        resourceId: slot.resourceId,
        gymMemberId,
        startsAt: slot.startsAt,
        durationMinutes: slot.durationMinutes,
      })
      setSlot(null)
    } catch (error) {
      setFailure(errorMessage(error))
    }
  }

  async function cancel() {
    if (cancelling === null) return
    setFailure(null)
    try {
      await mutations.cancel.mutateAsync({ bookingId: cancelling.bookingId })
      setCancelling(null)
    } catch (error) {
      setFailure(errorMessage(error))
    }
  }

  return (
    <>
      <PageHeader
        title="Servizi"
        description="Sauna, solarium, campi: chi può usarli e chi li ha prenotati."
        actions={
          service !== null ? (
            <Badge variant="neutral">{plural(freeCount, 'slot libero', 'slot liberi')}</Badge>
          ) : null
        }
      />

      {/*
        Due schede: il **catalogo** — cosa esiste — e la **griglia** di chi ha prenotato cosa.
        Prima del 20 agosto la prima non c'era, e lo stato vuoto di questa pagina lo diceva a
        parole: «si creano dall'API». Una lacuna scritta in un messaggio all'utente è comunque
        una lacuna.
      */}
      <Tabs variant="pill" defaultValue="grid">
        <TabsList>
          <TabsTrigger value="grid">
            <CalendarRange /> Prenotazioni
          </TabsTrigger>
          <TabsTrigger value="register">
            <ClipboardList /> Registro
          </TabsTrigger>
          <TabsTrigger value="catalog">
            <Waves /> Catalogo
          </TabsTrigger>
        </TabsList>

        {/* Tre schede, e la differenza fra le prime due è la domanda che si porta: la
            **griglia** dice se una cabina è libera adesso — quella del banco —, il
            **registro** dice chi viene oggi e chi non si è presentato — quella di chi apre
            la giornata. Sono la stessa area vista da due lati, e tenerle separate evita di
            cercare le persone in una griglia di slot. */}
        <TabsContent value="register">
          <ServiceBookingsPanel canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="catalog">
          <ServiceCatalogPanel canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="grid">
          {services.isLoading ? (
            <Skeleton className="h-40" />
          ) : services.isError ? (
            <Alert variant="danger" title="I servizi non si leggono">
              {errorMessage(services.error)}
            </Alert>
          ) : bookable.length === 0 ? (
            <EmptyState
              icon={<Ticket />}
              title="Nessun servizio su prenotazione"
              description={
                rows.length === 0
                  ? 'Non c’è ancora nessun servizio: creane uno dalla scheda «Catalogo».'
                  : 'I servizi esistenti sono inclusi o a consumo: nessuno di loro occupa uno slot, quindi non c’è una griglia da mostrare.'
              }
            />
          ) : (
            <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
              <nav
                className="border-border bg-card h-fit rounded-lg border p-2"
                aria-label="Servizi"
              >
                <ul className="space-y-1">
                  {bookable.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedId(row.id)
                          setDuration(undefined)
                        }}
                        className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
                          row.id === selected ? 'bg-muted font-medium' : 'hover:bg-muted/60'
                        }`}
                      >
                        <span className="block truncate">{row.name}</span>
                        <span className="text-muted-foreground block text-xs">
                          {KIND_LABELS[row.kind]} · {ACCESS_LABELS[row.accessMode]}
                          {row.resourceCount === undefined
                            ? ''
                            : ` · ${plural(row.resourceCount, 'risorsa', 'risorse')}`}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>

              <section className="border-border bg-card rounded-lg border p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setDay(addDays(day, -1))}>
                      <ChevronLeft />
                      <span className="sr-only">Giorno precedente</span>
                    </Button>
                    <span className="text-sm font-medium first-letter:uppercase">{label}</span>
                    <Button variant="outline" size="sm" onClick={() => setDay(addDays(day, 1))}>
                      <ChevronRight />
                      <span className="sr-only">Giorno successivo</span>
                    </Button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setDay(new Date())}>
                    <CalendarDays />
                    Oggi
                  </Button>
                </div>

                {service?.kind === 'court' && resources.length > 0 ? (
                  <Field label="Durata della prenotazione" className="mt-3">
                    {(p) => (
                      <Select
                        {...p}
                        value={duration === undefined ? '' : String(duration)}
                        onValueChange={(v) => setDuration(v ? Number(v) : undefined)}
                        items={[
                          { value: '', label: 'Durata predefinita del campo' },
                          ...[
                            ...new Set(
                              resources.flatMap((r) => r.durationOptionsMinutes ?? [r.slotMinutes]),
                            ),
                          ]
                            .sort((a, b) => a - b)
                            .map((n) => ({ value: String(n), label: `${n} minuti` })),
                        ]}
                      />
                    )}
                  </Field>
                ) : null}
                {failure !== null ? (
                  <Alert
                    variant="danger"
                    className="mt-3"
                    title="Il gesto non è andato a buon fine"
                  >
                    {failure}
                  </Alert>
                ) : null}

                {availability.isLoading ? (
                  <Skeleton className="mt-4 h-40" />
                ) : availability.isError ? (
                  <Alert variant="danger" className="mt-4" title="La disponibilità non si legge">
                    {errorMessage(availability.error)}
                  </Alert>
                ) : resources.length === 0 ? (
                  <EmptyState
                    className="mt-4"
                    icon={<Ticket />}
                    title="Nessuna risorsa"
                    description="Aggiungi un campo o una risorsa dalla scheda Catalogo."
                  />
                ) : (
                  <div className="mt-4 space-y-4">
                    {resources.map((resource) => (
                      <div key={resource.resourceId}>
                        <div className="mb-2 flex items-baseline justify-between gap-2">
                          <h3 className="text-sm font-medium">{resource.resourceName}</h3>
                          <span className="text-muted-foreground text-xs">
                            {resource.locationName ? `${resource.locationName} · ` : ''}
                            {duration ?? resource.slotMinutes} minuti · {resource.timezone ?? 'UTC'}
                          </span>
                        </div>
                        {resource.slots.length === 0 ? (
                          // Nessuno slot **non** è «tutto pieno»: è il servizio chiuso in
                          // questo giorno, e dirlo evita che qualcuno riprovi fra un'ora.
                          <p className="text-muted-foreground text-sm">
                            Nessun orario per questa durata e giornata.
                          </p>
                        ) : (
                          <ul className="flex flex-wrap gap-1.5">
                            {resource.slots.map((s) => (
                              <SlotButton
                                key={s.startsAt}
                                slot={s}
                                timezone={resource.timezone}
                                canWrite={canWrite}
                                onBook={() =>
                                  setSlot({
                                    resourceId: resource.resourceId,
                                    resourceName: resource.resourceName,
                                    startsAt: s.startsAt,
                                    durationMinutes:
                                      (new Date(s.endsAt).getTime() -
                                        new Date(s.startsAt).getTime()) /
                                      60000,
                                    timezone: resource.timezone,
                                  })
                                }
                                onCancel={(bookingId) =>
                                  setCancelling({
                                    bookingId,
                                    label: `${resource.resourceName}, ${formatTime(s.startsAt, resource.timezone)}`,
                                  })
                                }
                              />
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}

          <Dialog open={slot !== null} onOpenChange={(open) => !open && setSlot(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Prenota {slot?.resourceName}</DialogTitle>
                <DialogDescription>
                  {slot === null
                    ? null
                    : `${formatDateTime(slot.startsAt, slot.timezone)} · ${slot.durationMinutes} minuti`}
                </DialogDescription>
              </DialogHeader>
              <DialogBody>
                <MemberPicker
                  busy={mutations.book.isPending}
                  serviceId={service?.id ?? null}
                  onPick={(id) => void book(id)}
                />
              </DialogBody>
            </DialogContent>
          </Dialog>

          <Dialog open={cancelling !== null} onOpenChange={(open) => !open && setCancelling(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Disdire la prenotazione?</DialogTitle>
                <DialogDescription>
                  {cancelling?.label}. Lo slot torna libero subito
                  {service?.accessMode === 'booking_and_consumption'
                    ? ', e l’ingresso torna nel carnet.'
                    : '.'}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCancelling(null)}>
                  Lascia com’è
                </Button>
                <Button
                  variant="danger"
                  loading={mutations.cancel.isPending}
                  onClick={() => void cancel()}
                >
                  Disdici
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </>
  )
}

function formatTime(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
    ...(timeZone && timeZone !== 'UTC' ? { timeZoneName: 'shortOffset' as const } : {}),
  })
}

function formatDateTime(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    timeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function SlotButton({
  slot,
  timezone,
  canWrite,
  onBook,
  onCancel,
}: {
  slot: ServiceSlot
  timezone?: string
  canWrite: boolean
  onBook: () => void
  onCancel: (bookingId: Uuid) => void
}) {
  const time = formatTime(slot.startsAt, timezone)
  if (slot.available) {
    return (
      <li>
        <Button size="sm" variant="outline" disabled={!canWrite} onClick={onBook}>
          {time}
        </Button>
      </li>
    )
  }
  return (
    <li>
      <Button
        size="sm"
        variant="ghost"
        className="text-muted-foreground line-through"
        disabled={!canWrite || slot.bookingId === null}
        onClick={() => slot.bookingId !== null && onCancel(slot.bookingId)}
      >
        {time}
      </Button>
    </li>
  )
}

/**
 * Chi prenota si cerca per cognome, come ovunque nel pannello.
 *
 * Sotto il nome compare il **carnet**, quando quel servizio lo consuma (SRV-04):
 * è l'unico momento in cui «gliene restano due» serve a qualcosa, e chiederlo
 * dopo aver prenotato significherebbe scoprire troppo tardi che erano zero.
 */
function MemberPicker({
  busy,
  serviceId,
  onPick,
}: {
  busy: boolean
  serviceId: Uuid | null
  onPick: (id: Uuid) => void
}) {
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState<Uuid | null>(null)
  const members = useGymMembers({
    query: query.trim().length >= 2 ? query.trim() : undefined,
    limit: 5,
  })
  const credits = useGymMemberServiceCredits(highlighted)
  const results = query.trim().length >= 2 ? (members.data?.data ?? []) : []
  const wallet =
    serviceId === null ? undefined : credits.data?.data.find((row) => row.serviceId === serviceId)

  return (
    <div>
      <Field label="Chi prenota" hint="Cerca per cognome o nome, da due lettere.">
        {(props) => (
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2" />
            <Input
              {...props}
              autoFocus
              value={query}
              className="pl-8"
              placeholder="Cognome o nome"
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        )}
      </Field>

      {results.length > 0 ? (
        <ul className="mt-3 space-y-1">
          {results.map((member) => (
            <li key={member.id} className="flex items-center justify-between gap-2">
              <button
                type="button"
                className="hover:bg-muted/60 min-w-0 flex-1 truncate rounded-md px-2 py-1 text-left text-sm"
                onMouseEnter={() => setHighlighted(member.id)}
                onFocus={() => setHighlighted(member.id)}
                onClick={() => setHighlighted(member.id)}
              >
                {member.lastName} {member.firstName}
                {highlighted === member.id && wallet !== undefined ? (
                  <span className="text-muted-foreground ml-2 text-xs">
                    {plural(wallet.remainingUnits, 'ingresso', 'ingressi')} nel carnet
                  </span>
                ) : null}
              </button>
              <Button size="sm" disabled={busy} onClick={() => onPick(member.id)}>
                <UserPlus />
                Prenota
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
